"use client";

import "./styles.css";

import {
  DatePicker,
  type DatePickerProps,
  LocaleKeys,
  type LocalizedText,
  locales,
} from "@ids/react-date-picker";
import { HelpMessage } from "@ids/react-help-message";
import classNames from "classnames";
import { useLanguageFromUrl } from "@/hooks/useLanguageFromUrl";
import { ErrorMessage } from "../errorMessage/ErrorMessage";
import { Popover } from "../popover/Popover";

type ComposedDatePickerProps = Omit<
  DatePickerProps,
  "onChange" | "value" | "locale" | "localization"
> & {
  necessity?: "is-required" | "is-optional";
  popoverHeader?: string | React.ReactNode;
  popoverMessage?: string | React.ReactNode;
  dataTestid?: string;
  onChange?: (value: string) => void;
  value?: string | null;
};

export const ComposedDatePicker = ({
  id,
  children,
  necessity,
  disabled,
  helpMessage,
  errorMessage,
  className,
  popoverHeader,
  popoverMessage,
  onChange,
  value,
  dataTestid,
  ...props
}: ComposedDatePickerProps) => {
  const necessityClass = necessity ?? "";
  const disabledClass = disabled ? "is-disabled" : "";
  const errorMessageId = `${id}-error-message`;
  const helpMessageId = `${id}-help-message`;
  const labelId = `${id}-label`;
  const invalid = !!errorMessage;
  const ariaDescribedby = invalid ? errorMessageId : helpMessageId;
  const hiddenStyles: React.CSSProperties = {
    width: 0,
    height: 0,
    position: "absolute",
    visibility: "hidden",
  };

  const language = useLanguageFromUrl();
  const languageKey = {
    sv: LocaleKeys.SV_SE,
    fi: LocaleKeys.FI_FI,
    en: LocaleKeys.EN_GB,
  }[language];

  // fi format is day.month.year
  const dateAdapter = {
    format: (date: Date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    },
    parse: (value: string) => {
      const [dayStr, monthStr, yearStr] = value.split(".");
      const day = Number(dayStr);
      const month = Number(monthStr);
      const year = Number(yearStr);
      if (
        !day ||
        !month ||
        !year ||
        Number.isNaN(day) ||
        Number.isNaN(month) ||
        Number.isNaN(year) ||
        yearStr.length !== 4
      ) {
        return null;
      }
      return new Date(year, month - 1, day);
    },
  };

  const localization: LocalizedText = locales[languageKey ?? LocaleKeys.FI_FI];

  return (
    <div className={classNames("composed-date-picker", className)}>
      <label
        id={labelId}
        className={`if label flex-row align-items-center justify-content-space-between ${necessityClass} ${disabledClass}`}
        htmlFor={id}
      >
        <span>{children}</span>
        <Popover
          popoverHeader={popoverHeader}
          popoverMessage={popoverMessage}
        />
      </label>
      <DatePicker
        required={necessity === "is-required"}
        aria-labelledby={labelId}
        aria-describedby={ariaDescribedby}
        invalid={invalid}
        inputLabel=""
        locale={languageKey ?? LocaleKeys.FI_FI}
        localization={localization}
        dateAdapter={dateAdapter}
        disabled={disabled}
        onValueChange={(event) => {
          onChange?.(event.valueAsString);
        }}
        value={value ?? undefined}
        data-testid={dataTestid}
        {...props}
      />
      {!invalid && helpMessage !== undefined && (
        <HelpMessage
          id={helpMessageId}
          disabled={disabled}
          data-testid={dataTestid ? `${dataTestid}-help-message` : undefined}
        >
          {helpMessage}
        </HelpMessage>
      )}
      {errorMessage && (
        <ErrorMessage
          id={errorMessageId}
          style={invalid ? undefined : hiddenStyles}
          data-testid={dataTestid ? `${dataTestid}-error-message` : undefined}
        >
          {errorMessage}
        </ErrorMessage>
      )}
    </div>
  );
};
